import { getSnapshotPnlMetrics, pctFromBaseline } from './benchmarkEquitySeries';

/**
 * Normalize transfer_date / created_at style values to YYYY-MM-DD.
 */
export function transferDateKey(value) {
  if (!value) return null;
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Signed transfer amount: positive = deposit/in, negative = withdrawal/out.
 */
export function signedTransferAmount(transfer) {
  if (!transfer) return 0;
  const raw = Number(transfer.amount);
  if (!Number.isFinite(raw)) return 0;
  const direction = String(transfer.direction || '').toLowerCase();
  if (direction === 'out' || direction === 'withdrawal') return -Math.abs(raw);
  if (direction === 'in' || direction === 'deposit') return Math.abs(raw);
  return raw;
}

/**
 * Drop repeated rows so a transfer is never applied twice when lists are merged.
 */
export function dedupeTransfers(transfers) {
  if (!Array.isArray(transfers)) return [];
  const seen = new Set();
  return transfers.filter((t) => {
    if (t?.id == null) return true;
    if (seen.has(t.id)) return false;
    seen.add(t.id);
    return true;
  });
}

/**
 * Sum signed transfers with transfer_date in [since, until] inclusive.
 */
export function sumTransfersInRange(transfers, since, until) {
  if (!Array.isArray(transfers) || !transfers.length) return 0;
  let sum = 0;
  for (const t of dedupeTransfers(transfers)) {
    const key = transferDateKey(t.transfer_date || t.transferDate || t.date);
    if (!key) continue;
    if (since && key < since) continue;
    if (until && key > until) continue;
    sum += signedTransferAmount(t);
  }
  return sum;
}

/**
 * Net signed transfer per date (not cumulative).
 * @returns {Map<string, number>} date -> signed sum on that date
 */
export function netTransfersByDate(transfers, since = null, until = null) {
  const byDate = new Map();
  if (!Array.isArray(transfers)) return byDate;

  for (const t of dedupeTransfers(transfers)) {
    const key = transferDateKey(t.transfer_date || t.transferDate || t.date);
    if (!key) continue;
    if (since && key < since) continue;
    if (until && key > until) continue;
    byDate.set(key, (byDate.get(key) || 0) + signedTransferAmount(t));
  }

  return byDate;
}

/**
 * Cumulative signed transfers by date (sorted ascending).
 * @returns {Map<string, number>} date -> cumulative sum through that date
 */
export function cumulativeTransfersByDate(transfers, since = null, until = null) {
  const byDate = netTransfersByDate(transfers, since, until);
  const dates = [...byDate.keys()].sort();
  const cumulative = new Map();
  let running = 0;
  for (const date of dates) {
    running += byDate.get(date) || 0;
    cumulative.set(date, running);
  }
  return cumulative;
}

function cumulativeThrough(cumulativeByDate, dateKey) {
  if (!cumulativeByDate?.size || !dateKey) return 0;
  let last = 0;
  const dates = [...cumulativeByDate.keys()].sort();
  for (const date of dates) {
    if (date > dateKey) break;
    last = cumulativeByDate.get(date) || 0;
  }
  return last;
}

/**
 * Apply deposit/withdrawal adjustment to an equity curve for true trading P&L%.
 *
 * adjustedEquity_t = equity_t - cumulativeTransfers(rangeStart..t)
 * pnlPct_t = % change of adjusted equity from first adjusted baseline
 * dayPnlPct_t = day-over-day % change of adjusted equity (account % for the day)
 *
 * @param {Array<{ time: string, equity?: number, pnlPct?: number }>} equityCurve
 * @param {Array} transfers
 * @param {{ rangeStart?: string|null, rangeEnd?: string|null }} [opts]
 */
export function applyTransferAdjustedPnl(equityCurve, transfers, opts = {}) {
  if (!Array.isArray(equityCurve) || !equityCurve.length) {
    return { curve: equityCurve || [], netTransfers: 0, adjustedBaseline: null };
  }

  const rangeStart = opts.rangeStart || equityCurve[0]?.time || null;
  const rangeEnd = opts.rangeEnd || equityCurve[equityCurve.length - 1]?.time || null;
  const byDate = netTransfersByDate(transfers, rangeStart, rangeEnd);
  const cumulative = cumulativeTransfersByDate(transfers, rangeStart, rangeEnd);
  const netTransfers = sumTransfersInRange(transfers, rangeStart, rangeEnd);

  const withAdjusted = equityCurve.map((point) => {
    const equity = Number(point.equity);
    const hasEquity = Number.isFinite(equity);
    const cum = cumulativeThrough(cumulative, point.time);
    const adjustedEquity = hasEquity ? equity - cum : null;
    return {
      ...point,
      transferAmount: byDate.get(point.time) || 0,
      adjustedEquity,
      cumulativeTransfers: cum,
    };
  });

  const baseline =
    withAdjusted.find((p) => p.adjustedEquity != null && p.adjustedEquity > 0)?.adjustedEquity ??
    withAdjusted.find((p) => p.adjustedEquity != null)?.adjustedEquity ??
    null;

  let prevAdjusted = null;
  const curve = withAdjusted.map((point) => {
    const adjustedEquity = point.adjustedEquity;
    const pnlPct =
      adjustedEquity != null && baseline != null && baseline > 0
        ? pctFromBaseline(adjustedEquity, baseline)
        : typeof point.pnlPct === 'number'
          ? point.pnlPct
          : null;

    let dayPnlPct = null;
    if (adjustedEquity != null && prevAdjusted != null && prevAdjusted > 0) {
      dayPnlPct = pctFromBaseline(adjustedEquity, prevAdjusted);
    } else if (adjustedEquity != null && prevAdjusted == null) {
      dayPnlPct = 0;
    }
    if (adjustedEquity != null) prevAdjusted = adjustedEquity;

    return {
      ...point,
      pnlPct,
      dayPnlPct,
      adjustedEquity,
    };
  });

  return { curve, netTransfers, adjustedBaseline: baseline };
}

/**
 * Account P&L for the selected range with deposits/withdrawals removed, so a $10k
 * deposit is not reported as $10k of performance. Mirrors the equity chart headline.
 *
 * @param {{ equityCurve?: Array, rangeStart?: string|null, rangeEnd?: string|null, accountPnlPct?: number|null }} equityData
 * @param {Array} transfers
 * @returns {{ amount: number|null, pct: number|null, baselineEquity: number|null, endEquity: number|null, netTransfers: number }}
 */
export function getTransferAdjustedSnapshotPnl(equityData, transfers) {
  const raw = getSnapshotPnlMetrics(equityData);
  const curve = equityData?.equityCurve ?? [];
  if (!curve.length) return { ...raw, netTransfers: 0 };

  const rangeStart = equityData?.rangeStart || curve[0]?.time || null;
  const rangeEnd = equityData?.rangeEnd || curve[curve.length - 1]?.time || null;
  // A deposit and an offsetting withdrawal net to zero yet still move the baseline,
  // so key off whether any transfer landed in the range rather than off the net.
  if (!netTransfersByDate(transfers, rangeStart, rangeEnd).size) {
    return { ...raw, netTransfers: 0 };
  }
  const netTransfers = sumTransfersInRange(transfers, rangeStart, rangeEnd);

  const { curve: adjusted, adjustedBaseline } = applyTransferAdjustedPnl(curve, transfers, {
    rangeStart,
    rangeEnd,
  });
  const last = adjusted[adjusted.length - 1];
  const endEquity = last?.adjustedEquity ?? (Number.isFinite(Number(last?.equity)) ? Number(last.equity) : null);
  if (endEquity == null || adjustedBaseline == null) return { ...raw, netTransfers };

  return {
    amount: endEquity - adjustedBaseline,
    // The server's accountPnlPct is unadjusted, so it can't be trusted once money moved.
    pct: pctFromBaseline(endEquity, adjustedBaseline),
    baselineEquity: adjustedBaseline,
    endEquity,
    netTransfers,
  };
}
